/**
 * Client-side FlowV2 channel helpers. Mirrors CompilerCore FlowChannelConfig bootstrap.
 * Prefer GET Apps/Modules/InstalledChannels (UIExtensions). Builtins = fallback + API sentinel.
 */

export const CHANNEL_TYPE_API = 'API';
export const CHANNEL_TYPE_CALL = 'Call';
export const DEBUGGER_WEBRTC = 'webrtc';
export const DEBUGGER_NONE = 'none';

const CALL_MEDIA_SCHEMA = '/schema/public/Platform.Schema.ChannelEnvelope.v1/Call';

/** Names only. A missing ReturnType and Parameters means the signature is not specified yet. */
const CALL_FLOW_EVENTS = [
    { Name: 'CallDisconnect' },
    { Name: 'DeliveryAccepted' },
];

function cloneFlowEvents(list) {
    if (!Array.isArray(list))
        return [];
    return list.map((event) => {
        if (!event || typeof event !== 'object')
            return { Name: '' };
        const copy = { ...event };
        if (Array.isArray(event.Parameters))
            copy.Parameters = event.Parameters.map((row) => ({ ...(row || {}) }));
        return copy;
    });
}

const BUILTIN = {
    [CHANNEL_TYPE_API]: {
        ChannelType: CHANNEL_TYPE_API,
        Description: 'API-only',
        Icon: 'mdi-api',
        FlowDebugger: DEBUGGER_NONE,
        HasMedia: false,
        FlowTouchpointListURL: null,
        FlowTouchpointSaveURL: null,
    },
    [CHANNEL_TYPE_CALL]: {
        ChannelType: CHANNEL_TYPE_CALL,
        Description: 'Telephone calls',
        Icon: 'mdi-phone',
        FlowProjectRef: '$VoiceExtensions3.prj',
        FlowMediaType: 'Platform.VoiceExt.v3',
        FlowDebugger: DEBUGGER_WEBRTC,
        FlowDebugMediaSchema: CALL_MEDIA_SCHEMA,
        FlowDebugProjectRef: '$IVRInboundHandler.prj',
        FlowDebugClass: 'Platform.FlowV2Handler',
        FlowDebugMethod: 'StartDebugging',
        HasMedia: true,
        FlowTouchpointListURL: 'Apps/FlowV2/ListTouchpointsByFlow',
        FlowTouchpointSaveURL: 'Apps/FlowV2/SaveTouchpointFlowV2',
        FlowEvents: CALL_FLOW_EVENTS,
    },
    SMS: {
        ChannelType: 'SMS',
        Description: 'SMS',
        Icon: 'mdi-message',
        FlowProjectRef: '$SMSBase.prj',
        FlowMediaType: 'Platform.SMSBase',
        FlowDebugger: DEBUGGER_NONE,
        FlowDebugMediaSchema: '/schema/public/Platform.Schema.ChannelEnvelope.v1/SMS',
        FlowDebugProjectRef: '$SMSInboundHandler.prj',
        FlowDebugClass: 'Platform.FlowV2SMSHandler',
        FlowDebugMethod: 'StartDebugging',
        HasMedia: true,
        FlowTouchpointListURL: null,
        FlowTouchpointSaveURL: null,
        FlowEvents: [],
    },
    EMail: {
        ChannelType: 'EMail',
        Description: 'Email',
        Icon: 'mdi-email',
        FlowProjectRef: '$EMailBase.prj',
        FlowMediaType: 'Platform.EMailBase_v2',
        FlowDebugger: DEBUGGER_NONE,
        FlowDebugMediaSchema: '/schema/public/Platform.Schema.ChannelEnvelope.v1/Email',
        FlowDebugProjectRef: '$EmailInboundHandler.prj',
        FlowDebugClass: 'Platform.FlowV2EmailHandler',
        FlowDebugMethod: 'StartDebugging',
        HasMedia: true,
        FlowTouchpointListURL: null,
        FlowTouchpointSaveURL: null,
        FlowEvents: [],
    },
};

function withFlowEvents(row) {
    return { ...row, FlowEvents: cloneFlowEvents(row?.FlowEvents) };
}

export function apiChannelSentinel() {
    return withFlowEvents(BUILTIN[CHANNEL_TYPE_API]);
}

export function builtinChannelFallbackList() {
    return [
        apiChannelSentinel(),
        withFlowEvents(BUILTIN[CHANNEL_TYPE_CALL]),
        withFlowEvents(BUILTIN.SMS),
        withFlowEvents(BUILTIN.EMail),
    ];
}

/**
 * Normalize InstalledChannels payload and prepend API sentinel.
 * Skips rows with FlowDesignerEnabled === false; includes when field is absent.
 */
export function mergeInstalledChannels(raw) {
    let list = raw;
    if (raw && !Array.isArray(raw) && Array.isArray(raw.Result))
        list = raw.Result;
    if (!Array.isArray(list))
        list = [];

    const byType = new Map();
    for (const row of list) {
        if (!row?.ChannelType || typeof row.ChannelType !== 'string')
            continue;
        if (row.FlowDesignerEnabled === false)
            continue;
        const ct = row.ChannelType.trim();
        if (!ct || byType.has(ct))
            continue;
        byType.set(ct, row);
    }

    const rows = [...byType.values()];
    if (![...byType.keys()].some(k => k === CHANNEL_TYPE_API))
        rows.unshift(apiChannelSentinel());
    return rows;
}

/** Load channel list from InstalledChannels; fall back to builtins on failure/empty. */
export async function loadFlowChannelList(apiGet) {
    try {
        const raw = await apiGet('Apps/Modules/InstalledChannels');
        const merged = mergeInstalledChannels(raw);
        if (merged.length > 0)
            return merged;
    }
    catch (e) {
        /* fall through */
    }
    return builtinChannelFallbackList();
}

export function normalizeChannelType(channelType) {
    if (!channelType || typeof channelType !== 'string' || !channelType.trim())
        return CHANNEL_TYPE_CALL;
    return channelType.trim();
}

function pickFlowDebug(row, fallback) {
    return {
        FlowDebugMediaSchema: row?.FlowDebugMediaSchema || fallback?.FlowDebugMediaSchema || null,
        FlowDebugProjectRef: row?.FlowDebugProjectRef || fallback?.FlowDebugProjectRef || null,
        FlowDebugClass: row?.FlowDebugClass || fallback?.FlowDebugClass || null,
        FlowDebugMethod: row?.FlowDebugMethod || fallback?.FlowDebugMethod || null,
    };
}

export function resolveChannel(channelType, list) {
    const ct = normalizeChannelType(channelType);
    const builtin = BUILTIN[ct] || BUILTIN[CHANNEL_TYPE_CALL];
    if (Array.isArray(list)) {
        const row = list.find(c => c && c.ChannelType === ct);
        if (row) {
            const hasMedia = !!(row.FlowProjectRef && row.FlowMediaType);
            return {
                ChannelType: ct,
                Description: row.Description || ct,
                Icon: row.Icon || builtin?.Icon || 'mdi-folder-file-outline',
                FlowProjectRef: row.FlowProjectRef || null,
                FlowMediaType: row.FlowMediaType || null,
                FlowDebugger: row.FlowDebugger || DEBUGGER_NONE,
                ...pickFlowDebug(row, builtin),
                HasMedia: hasMedia,
                FlowTouchpointListURL: row.FlowTouchpointListURL ?? builtin?.FlowTouchpointListURL ?? null,
                FlowTouchpointSaveURL: row.FlowTouchpointSaveURL ?? builtin?.FlowTouchpointSaveURL ?? null,
                FlowTouchpointEditor: row.FlowTouchpointEditor ?? builtin?.FlowTouchpointEditor ?? null,
                FlowEvents: Array.isArray(row.FlowEvents)
                    ? cloneFlowEvents(row.FlowEvents)
                    : cloneFlowEvents(builtin?.FlowEvents),
            };
        }
    }
    return withFlowEvents(builtin);
}

export function projectTreeIcon(channelType, list) {
    return resolveChannel(channelType, list).Icon || 'mdi-folder-file-outline';
}

export function projectTreeTooltip(channelType, list) {
    const c = resolveChannel(channelType, list);
    return c.Description || c.ChannelType || 'Call';
}

/** Distinct non-null FlowMediaType values from channel list (fallback: builtins). */
export function knownMediaTypes(list) {
    const set = new Set();
    if (Array.isArray(list)) {
        for (const row of list) {
            if (row?.FlowMediaType && typeof row.FlowMediaType === 'string' && row.FlowMediaType.trim())
                set.add(row.FlowMediaType.trim());
        }
    }
    if (set.size === 0) {
        for (const row of Object.values(BUILTIN)) {
            if (row.FlowMediaType)
                set.add(row.FlowMediaType);
        }
    }
    return set;
}

/**
 * First argument ArgType/Type that is a registered media type; else null (universal tool).
 * @param {object} tool
 * @param {Set<string>|string[]} known
 */
export function paletteToolMediaType(tool, known) {
    const knownSet = known instanceof Set ? known : new Set(known || []);
    const args = Array.isArray(tool?.Arguments) ? tool.Arguments : [];
    for (const a of args) {
        if (!a)
            continue;
        const argType = a.ArgType ?? a.Type;
        if (typeof argType === 'string' && knownSet.has(argType))
            return argType;
    }
    return null;
}

/**
 * Whether a sibling FlowHeader/APIHeader from calleeChannelType may be dropped onto callerConfig's canvas.
 * API-only (no media) callees are allowed from any flow; media callees require the same FlowMediaType.
 */
/** Set on folder-tree project drags so the flowchart can reject them during dragover. */
export const FLOWV2_FOLDER_PROJECT_MIME = 'application/x-flowv2-folder-project';

export function isFolderProjectDrag(dataTransfer) {
    const types = dataTransfer?.types;
    if (!types)
        return false;
    return Array.from(types).includes(FLOWV2_FOLDER_PROJECT_MIME);
}

export function isSiblingMethodAllowed(callerConfig, calleeChannelType, list) {
    const callee = resolveChannel(calleeChannelType, list);
    if (!callee.HasMedia)
        return true;
    if (!callerConfig?.HasMedia)
        return false;
    return callerConfig.FlowMediaType === callee.FlowMediaType;
}

/**
 * Folder-tree method drag: API editors may pull any channel (Monaco needs the media arg).
 * Flowchart editors still use isSiblingMethodAllowed (canvas drop stays strict).
 */
export function isSiblingMethodDraggable(callerConfig, calleeChannelType, list, itemType) {
    if (itemType === 'APIHeader' || !callerConfig?.HasMedia)
        return true;
    return isSiblingMethodAllowed(callerConfig, calleeChannelType, list);
}

/** Physical reparent: missing channel matches the legacy Call default. */
export function isSameChannelForMove(sourceChannelType, destChannelType) {
    return normalizeChannelType(sourceChannelType) === normalizeChannelType(destChannelType);
}

function treeItemFullPath(item) {
    if (!item)
        return '';
    if (item.Type === 'Folder' && item.ID)
        return item.ID;
    const path = item.Path || '';
    const name = item.Name || '';
    if (!path)
        return name;
    if (!name)
        return path;
    return `${path}/${name}`;
}

/**
 * Whether a folder-tree row may be reparented onto a target.
 * `target` null (or Type 'root') is the tree root.
 * opts.sourceChannelType / destChannelType apply to class and method moves.
 * @returns {{ ok: boolean, reason: string|null }}
 */
export function canMoveToTarget(dragged, target, opts) {
    const options = opts || {};
    if (!dragged?.Type)
        return { ok: false, reason: 'Nothing to move' };

    const destType = !target || target.Type === 'root' ? 'root' : target.Type;
    if (target && dragged.ID && target.ID === dragged.ID)
        return { ok: false, reason: 'Cannot move an item onto itself' };

    if (dragged.Type === 'Folder') {
        if (destType !== 'Folder' && destType !== 'root')
            return { ok: false, reason: 'Cannot move a folder into a project' };
        if (destType === 'Folder') {
            const srcFull = options.sourceFullPath || treeItemFullPath(dragged);
            const destFull = options.destFullPath || treeItemFullPath(target);
            if (destFull === srcFull || (srcFull && destFull.startsWith(srcFull + '/')))
                return { ok: false, reason: 'Cannot move a folder into itself' };
        }
        return { ok: true, reason: null };
    }

    if (dragged.Type === 'Project') {
        if (destType !== 'Folder' && destType !== 'root')
            return { ok: false, reason: 'Drop a project on a folder' };
        return { ok: true, reason: null };
    }

    if (dragged.Type === 'Class') {
        if (destType !== 'Project')
            return { ok: false, reason: 'Drop a class on a project' };
        if (!isSameChannelForMove(options.sourceChannelType, options.destChannelType))
            return { ok: false, reason: 'Different channel — cannot move here' };
        return { ok: true, reason: null };
    }

    if (dragged.Type === 'FlowHeader' || dragged.Type === 'APIHeader') {
        if (destType !== 'Project' && destType !== 'Class')
            return { ok: false, reason: 'Drop a flowchart or API on a project or class' };
        if (!isSameChannelForMove(options.sourceChannelType, options.destChannelType))
            return { ok: false, reason: 'Different channel — cannot move here' };
        return { ok: true, reason: null };
    }

    return { ok: false, reason: 'This item cannot be moved' };
}

function lockedSignatureRow(name, type, kind) {
    return {
        Name: name,
        Type: type,
        Persistent: false,
        kind,
        locked: true,
    };
}

/**
 * Implicit Variables-panel rows, matching FlowChannelConfig.ImplicitArgumentDefs.
 * Media only when the channel has a media type; globals on every method.
 * @param {{ HasMedia?: boolean, FlowMediaType?: string }|null|undefined} channel
 * @returns {Array<{Name:string,Type:string,Persistent:boolean,kind:string,locked:true}>}
 */
export function implicitPanelVariables(channel) {
    const rows = [];
    const mediaType = channel?.FlowMediaType;
    if (channel?.HasMedia && mediaType)
        rows.push(lockedSignatureRow('media', mediaType, 'media'));
    rows.push(lockedSignatureRow('globals', 'json', 'globals'));
    return rows;
}

/**
 * Whether a palette tool should appear for the current channel.
 * Universal (no media arg) → always; API (no FlowMediaType) → deny media-bound; else match.
 */
export function isPaletteToolAllowed(tool, channelConfig, known) {
    const mediaType = paletteToolMediaType(tool, known);
    if (mediaType == null)
        return true;
    const channelMedia = channelConfig?.FlowMediaType;
    if (!channelMedia)
        return false;
    return mediaType === channelMedia;
}

/** Filter ListPalette groups → children by channel; drop empty groups. */
export function filterPaletteItems(groups, channelConfig, list) {
    const known = knownMediaTypes(list);
    if (!Array.isArray(groups))
        return [];
    return groups
        .map(g => {
            const children = Array.isArray(g?.children)
                ? g.children.filter(t => isPaletteToolAllowed(t, channelConfig, known))
                : [];
            return { ...g, children };
        })
        .filter(g => g.children.length > 0);
}
